import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Returns the highest valid quantity that can be selected without exceeding native or JBG limits.
 * Unlimited products retain Horizon's existing native-max behavior.
 *
 * @param {Object} config
 * @param {number} config.min
 * @param {number} config.step
 * @param {number | null} config.nativeMax
 * @param {number} config.variantCartQuantity
 * @param {number | null} config.purchaseLimit
 * @param {number} config.productCartQuantity
 * @param {number} [config.pendingProductQuantity]
 * @param {number} [config.pendingVariantQuantity]
 * @param {boolean} [config.subtractVariantCartQuantity]
 * @returns {number | null}
 */
export function getEffectiveQuantityMaximum({
  min,
  step,
  nativeMax,
  variantCartQuantity,
  purchaseLimit,
  productCartQuantity,
  pendingProductQuantity = 0,
  pendingVariantQuantity = 0,
  subtractVariantCartQuantity = true,
}) {
  let maximum = nativeMax;

  if (maximum !== null && subtractVariantCartQuantity) {
    maximum -= variantCartQuantity + pendingVariantQuantity;
  }

  if (purchaseLimit !== null) {
    const purchaseLimitMaximum = purchaseLimit - productCartQuantity - pendingProductQuantity;
    maximum = maximum === null ? purchaseLimitMaximum : Math.min(maximum, purchaseLimitMaximum);
  }

  if (maximum === null) return null;

  // Preserve Horizon's existing native behavior for products without a JBG limit.
  if (purchaseLimit === null) return Math.max(maximum, min);

  if (maximum < min) return 0;

  const safeStep = Math.max(step, 1);
  return min + Math.floor((maximum - min) / safeStep) * safeStep;
}

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   * @param {number} [productCartQty] - The quantity currently in cart for this product
   */
  setCartQuantity(cartQty, productCartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    if (productCartQty !== undefined) {
      this.refs.quantityInput.setAttribute('data-product-cart-quantity', productCartQty.toString());
    }
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @param {{quantity?: number, productCartQuantity?: number, variantCartQuantity?: number, pendingProductQuantity?: number, pendingVariantQuantity?: number}} [options]
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart(options = {}) {
    const { min, max, step, cartQuantity, productCartQuantity, purchaseLimit, value } = this.getCurrentValues();
    const quantityToAdd = options.quantity ?? value;

    if (purchaseLimit !== null) {
      const effectiveMax = this.getEffectiveMax(options);
      const canAdd =
        effectiveMax !== null &&
        effectiveMax >= min &&
        quantityToAdd >= min &&
        quantityToAdd <= effectiveMax &&
        (quantityToAdd - min) % step === 0;

      return {
        canAdd,
        maxQuantity: max === null ? purchaseLimit : Math.min(max, purchaseLimit),
        cartQuantity: options.productCartQuantity ?? productCartQuantity,
        quantityToAdd,
      };
    }

    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   * @param {string|null|undefined} [purchaseLimit] - JBG product purchase limit
   * @param {string|null|undefined} [productCartQuantity] - Current cart quantity for the product
   * @param {string|null|undefined} [productId] - Product ID associated with the limit
   */
  updateConstraints(min, max, step, purchaseLimit, productCartQuantity, productId) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max !== null && max !== '') {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    if (purchaseLimit !== undefined) {
      if (purchaseLimit === null) {
        quantityInput.removeAttribute('data-purchase-limit');
      } else {
        quantityInput.setAttribute('data-purchase-limit', purchaseLimit);
      }
    }
    if (productCartQuantity !== undefined) {
      if (productCartQuantity === null) {
        quantityInput.removeAttribute('data-product-cart-quantity');
      } else {
        quantityInput.setAttribute('data-product-cart-quantity', productCartQuantity);
      }
    }
    if (productId !== undefined) {
      if (productId === null) {
        quantityInput.removeAttribute('data-product-id');
      } else {
        quantityInput.setAttribute('data-product-id', productId);
      }
    }

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number, purchaseLimit: number|null, productCartQuantity: number, lineQuantity: number, productId: string|null}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
      purchaseLimit: parseIntOrDefault(quantityInput.getAttribute('data-purchase-limit'), null),
      productCartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-product-cart-quantity'), 0),
      lineQuantity: parseIntOrDefault(quantityInput.getAttribute('data-line-quantity'), 0),
      productId: quantityInput.getAttribute('data-product-id'),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @param {{productCartQuantity?: number, variantCartQuantity?: number, pendingProductQuantity?: number, pendingVariantQuantity?: number}} [options]
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax(options = {}) {
    const { min, max, step, cartQuantity, purchaseLimit, productCartQuantity } = this.getCurrentValues();

    return getEffectiveQuantityMaximum({
      min,
      step,
      nativeMax: max,
      variantCartQuantity: options.variantCartQuantity ?? cartQuantity,
      purchaseLimit,
      productCartQuantity: options.productCartQuantity ?? productCartQuantity,
      pendingProductQuantity: options.pendingProductQuantity,
      pendingVariantQuantity: options.pendingVariantQuantity,
    });
  }

  /**
   * Gets the validated JBG purchase-limit state for this selector.
   * @returns {{productId: string, purchaseLimit: number, productCartQuantity: number, variantCartQuantity: number, min: number, max: number|null, step: number}|null}
   */
  getPurchaseLimitState() {
    const { productId, purchaseLimit, productCartQuantity, cartQuantity, min, max, step } = this.getCurrentValues();
    if (!productId || purchaseLimit === null) return null;

    return {
      productId,
      purchaseLimit,
      productCartQuantity,
      variantCartQuantity: cartQuantity,
      min,
      max,
      step,
    };
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value, purchaseLimit } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (purchaseLimit !== null && stepMultiplier > 0 && (effectiveMax === null || effectiveMax < min || value >= effectiveMax)) {
      this.updateButtonStates();
      return;
    }

    const newValue =
      purchaseLimit !== null && stepMultiplier < 0
        ? Math.max(min, value + step * stepMultiplier)
        : Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step, purchaseLimit } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const parsedQuantity = Math.max(min, parseInt(event.target.value) || 0);
    const quantity =
      purchaseLimit !== null && effectiveMax !== null && effectiveMax < min
        ? min
        : Math.min(effectiveMax ?? Infinity, parsedQuantity);

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value, purchaseLimit } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue =
      purchaseLimit !== null && effectiveMax !== null && effectiveMax < min
        ? min
        : Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
